import { Box, CircularProgress, List, ListItemButton, ListItemText, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface TocItem {
  id: string;
  title: string;
}

interface EulaContentProps {
  showTableOfContents?: boolean;
  onError?: (error: Error | null) => void;
  onLoading?: (isLoading: boolean) => void;
}

export function EulaContent({ showTableOfContents = false, onError, onLoading }: EulaContentProps) {
  const {
    data: eulaContent,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['eula'],
    queryFn: async () => {
      const eulaUrl = import.meta.env.VITE_REACT_APP_EULA_URL;
      const response = await fetch(eulaUrl);

      if (!response.ok) {
        throw new Error(`Failed to fetch EULA: ${response.statusText}`);
      }

      return await response.text();
    },
  });

  useEffect(() => {
    onError?.(error);
    onLoading?.(isLoading);
  }, [error, isLoading, onError, onLoading]);

  const toc: TocItem[] = [];
  if (eulaContent) {
    const headers = eulaContent.match(/^##\s+(.+)$/gm) || [];
    headers.forEach((header) => {
      const title = header.replace(/^##\s+/, '');
      const id = title
        .toLowerCase()
        .replace(/^\d+\.\s*/, '')
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-');
      toc.push({ id, title });
    });
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant='h6' color='error'>
          Error loading EULA content
        </Typography>
        <Typography variant='body2' sx={{ mt: 1 }}>
          {error instanceof Error ? error.message : 'Failed to load EULA'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', gap: showTableOfContents ? 10 : 0 }}>
      {showTableOfContents && (
        <Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              background: '#fff',
              boxShadow: '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
              padding: '16px',
              borderRadius: '8px',
              width: '340px',
            }}
          >
            <Typography
              variant='body2'
              sx={{
                fontWeight: 500,
                mt: 2,
              }}
            >
              Table of Contents
            </Typography>
            <List sx={{ maxHeight: '100%', color: 'accentBlue.main' }}>
              {toc.map((item) => (
                <ListItemButton key={item.id} component='a' href={`#${item.id}`} sx={{ py: 0.5 }}>
                  <ListItemText
                    primary={item.title}
                    sx={{ '.MuiListItemText-primary': { fontSize: '14px !important' } }}
                  />
                </ListItemButton>
              ))}
            </List>
          </Box>
        </Box>
      )}
      <Box sx={{ flex: 1 }}>
        <Box
          sx={{
            '& h2': {
              fontSize: '20px',
              fontWeight: 'bold',
              marginTop: 3,
              marginBottom: 2,
            },
            '& h3': {
              fontSize: '16px',
              fontWeight: 'bold',
              marginTop: 2,
              marginBottom: 1,
              paddingLeft: 2,
            },
            '& p': {
              fontSize: '14px',
              marginBottom: 2,
            },
            '& strong': {
              fontWeight: 600,
            },
            '& a': {
              color: 'accentBlue.main',
              textDecoration: 'underline',
            },
          }}
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              h2: ({ children }) => {
                const text = String(children);
                const id = text
                  .toLowerCase()
                  .replace(/^\d+\.\s*/, '')
                  .replace(/[^a-z0-9\s-]/g, '')
                  .replace(/\s+/g, '-');
                return <h2 id={id}>{children}</h2>;
              },
              h3: ({ children }) => <h3 style={{ paddingLeft: '16px' }}>{children}</h3>,
              p: ({ children }) => {
                const text = String(children);
                if (text.startsWith('Last updated:')) {
                  return (
                    <Typography
                      variant='caption'
                      sx={{
                        fontWeight: 500,
                      }}
                    >
                      {children}
                    </Typography>
                  );
                }
                if (text.startsWith('PLEASE READ')) {
                  return (
                    <Typography
                      variant='body2'
                      sx={{
                        fontWeight: 500,
                        mt: 2,
                      }}
                    >
                      {children}
                    </Typography>
                  );
                }
                return (
                  <Typography variant='body2' sx={{ marginBottom: 2 }}>
                    {children}
                  </Typography>
                );
              },
            }}
          >
            {eulaContent}
          </ReactMarkdown>
        </Box>
      </Box>
    </Box>
  );
}
